// Hideo - the background of the app themes: a <canvas id="themeSky"> fixed behind the whole page (z-index -2, under the
// background layer's pattern and grain of src/features/backdrop.ts; no pointer events), only while a theme other than Default is shown (with Default the element does not exist).
// Ported from the user's ProCut background (cielo.js). Scenes (StyleDef.sky, src/features/appThemes.ts):
//   still   the theme's two background colours in a still diagonal gradient (Colours, High contrast, Fluent)
//   halos   Blurred bubbles: soft lights in the main colour and two neighbouring hues that sway slowly left and right
//   aurora  Frutiger Aero / Aero night: aqua background, sun (or moon) rays, a fan of threads, a trail and low glows
//   lines   Nostalgia Channel: thin horizontal lines and a soft grey band at the bottom
//   glow    Four Keys: a large green glow that breathes (one breath every ~12 s)
// About 30 frames a second, only for the moving scenes; the browser pauses it in a hidden tab. With the system's
// "reduce motion" every scene is drawn once and stays still. A theme change cross-fades only the background: a picture
// of the old one (toDataURL) is laid over the new one and fades out (snapshotSky, class sky-before in themes.css).
import { resolvedPalette, styleDef, type Palette, type StyleDef } from './appThemes'
import { hex2rgb, mixc, shiftHue } from './theme'
import { reducedMotion } from '../utils/media'

const SKY_ID = 'themeSky'
let canvas: HTMLCanvasElement | null = null
let g: CanvasRenderingContext2D | null = null
let cur: StyleDef | undefined
/** The palette of the theme shown, with the main colour (adaptive themes) */
let pal: Palette | undefined
/** Where the main colour comes from (the theme store's themeAccent, set by installSky) */
let accentOf: () => string | null = () => null
let t = 0, W = 0, H = 0, prev = 0, last = 0, raf = 0

/** The scene moves (and is redrawn about 30 times a second) */
const moving = (s: StyleDef | undefined): boolean => !!s && s.sky !== 'still' && !reducedMotion()

/** Shows the background of a theme (Default: removes it) and draws it at once */
export function syncSky(key: string, doc: Document = document): void {
  cur = styleDef(key)
  pal = resolvedPalette(key, accentOf())
  if (!cur) { stop(); canvas?.remove(); canvas = null; g = null; return }
  if (!canvas || !canvas.isConnected) {
    canvas = doc.createElement('canvas')
    canvas.id = SKY_ID
    canvas.setAttribute('aria-hidden', 'true')
    doc.body.prepend(canvas)
    g = canvas.getContext('2d')
    W = H = 0
  }
  canvas.classList.toggle('sky-blur', cur.sky === 'aurora')
  measure(true)
  if (moving(cur)) start(); else stop()
}

/** A picture of the background now on screen, laid over it to fade out (null without a background) */
function snapshotSky(doc: Document = document): HTMLImageElement | null {
  if (!canvas || !g) return null
  try {
    const img = doc.createElement('img')
    img.className = 'sky-before' + (canvas.classList.contains('sky-blur') ? ' sky-blur' : '')
    img.alt = ''
    img.src = canvas.toDataURL()
    canvas.after(img)
    return img
  } catch { return null }
}
/** Fades out and removes a picture made by snapshotSky */
function fadeSnapshot(img: HTMLImageElement | null): void {
  if (!img) return
  requestAnimationFrame(() => requestAnimationFrame(() => img.classList.add('sky-gone')))
  setTimeout(() => img.remove(), 1000)
}

function start(): void {
  if (raf) return
  prev = performance.now()
  raf = requestAnimationFrame(frame)
}
function stop(): void {
  if (raf) cancelAnimationFrame(raf)
  raf = 0
}
function frame(now: number): void {
  raf = requestAnimationFrame(frame)
  if (now - last < 33) return                       // ~30 frames a second: smooth and light
  t += Math.min(now - prev, 60) / 1000; prev = now; last = now
  draw()
}

/** Size of the canvas = its box on screen (at most 1.5 device pixels per pixel); resizing clears it, so it is redrawn */
function measure(force = false): void {
  if (!canvas || !g) return
  const dpr = Math.min(window.devicePixelRatio || 1, 1.5), w = canvas.clientWidth || window.innerWidth, h = canvas.clientHeight || window.innerHeight
  if (force || w !== W || h !== H) {
    W = w; H = h
    canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr)
    g.setTransform(dpr, 0, 0, dpr, 0, 0)
  }
  draw()
}

/** Installs the background: shown for the stored theme, redrawn on resize, following every theme change (theme store
 *  hooks) and the system's "reduce motion" */
export function installSky(win: Window, theme: { curStyle: () => string; themeAccent?: () => string
  hooks: { beforeSwitch?: () => void; afterSwitch?: (key: string, animate: boolean) => void; repaint?: (key: string) => void } }): void {
  if (theme.themeAccent) accentOf = theme.themeAccent
  syncSky(theme.curStyle())
  // A new main colour: the scene takes it at once (Blurred bubbles' lights, the gradients)
  const prevRepaint = theme.hooks.repaint
  theme.hooks.repaint = key => { prevRepaint?.(key); syncSky(key) }
  win.addEventListener('resize', () => measure())
  let before: HTMLImageElement | null = null
  const prevBefore = theme.hooks.beforeSwitch, prevAfter = theme.hooks.afterSwitch
  theme.hooks.beforeSwitch = () => { prevBefore?.(); before = reducedMotion() ? null : snapshotSky() }
  theme.hooks.afterSwitch = (key, animate) => {
    const fresh = !canvas
    syncSky(key)
    // From Default the new background fades in; to Default the old picture fades out
    if (fresh && canvas && animate && !reducedMotion()) {
      const c = canvas
      c.classList.add('sky-in')
      requestAnimationFrame(() => requestAnimationFrame(() => c.classList.remove('sky-in')))
    }
    fadeSnapshot(before); before = null
    prevAfter?.(key, animate)
  }
  if (typeof win.matchMedia === 'function') {
    const q = win.matchMedia('(prefers-reduced-motion: reduce)')
    q.addEventListener?.('change', () => syncSky(theme.curStyle()))
  }
}

// ===================================================================== drawing
/** A "breathing" point: x, y as fractions of the screen, moving slowly with time */
const pt = (x: number, y: number, ax = 0, ay = 0, v = 0.3, f = 0): [number, number] =>
  [W * (x + ax * Math.sin(t * v + f)), H * (y + ay * Math.sin(t * v * 1.3 + f))]

function draw(): void {
  if (!g || !cur || !pal || !W || !H) return
  g.globalCompositeOperation = 'source-over'
  if (cur.sky === 'aurora') {
    background(cur.key === 'aero_night')
    g.globalCompositeOperation = 'lighter'         // light adds up
    rays(cur.key === 'aero_night'); glows(cur.key === 'aero_night'); fan(cur.key === 'aero_night'); trail(cur.key === 'aero_night')
    g.globalCompositeOperation = 'source-over'
    return
  }
  // Every other theme: the still gradient of its two background colours, then its own scene on top
  const gr = g.createLinearGradient(0, 0, W, H)
  gr.addColorStop(0, pal.skyA); gr.addColorStop(1, pal.skyB)
  g.fillStyle = gr; g.fillRect(0, 0, W, H)
  if (cur.sky === 'halos') halos(!cur.dark, pal.accent)
  else if (cur.sky === 'lines') lines(cur.night)
  else if (cur.sky === 'glow') greenGlow(cur.night)
}

// ----- Frutiger Aero: aqua sky (Aero night: blue-black sky with a cold moon and an aurora from aqua to violet)
function background(night: boolean): void {
  const c = g!
  if (night) {
    const base = c.createLinearGradient(0, 0, W, H)
    base.addColorStop(0, '#01060c'); base.addColorStop(0.5, '#03141d'); base.addColorStop(1, '#071a2e')
    c.fillStyle = base; c.fillRect(0, 0, W, H)
    radial(-W * 0.1, H * 0.75, W * 0.7, '20,170,140', 0.35)
    radial(W * 1.05, H * 0.95, W * 0.6, '90,60,200', 0.38)
    return
  }
  const base = c.createLinearGradient(0, 0, W, H)
  base.addColorStop(0, '#0c6f5e'); base.addColorStop(0.45, '#0b6f78'); base.addColorStop(1, '#1785ad')
  c.fillStyle = base; c.fillRect(0, 0, W, H)
  const shade = c.createLinearGradient(0, 0, 0, H * 0.6)
  shade.addColorStop(0, 'rgba(3,40,50,.55)'); shade.addColorStop(1, 'rgba(3,40,50,0)')
  c.fillStyle = shade; c.fillRect(0, 0, W, H)
  radial(-W * 0.1, H * 0.7, W * 0.7, '120,220,90', 0.55)
  radial(W * 1.05, H * 0.95, W * 0.55, '60,200,255', 0.6)
}
function radial(x: number, y: number, r: number, rgb: string, a: number): void {
  const c = g!, gr = c.createRadialGradient(x, y, 0, x, y, r)
  gr.addColorStop(0, `rgba(${rgb},${a})`); gr.addColorStop(1, `rgba(${rgb},0)`)
  c.fillStyle = gr; c.fillRect(0, 0, W, H)
}

// Sun rays from the top left (moon from the top right at night), soft edges (each ray = 3 wedges), turning and pulsing
const RAYS = Array.from({ length: 11 }, (_, i) => ({
  ang: 0.18 + i * 0.105 + (i % 3) * 0.015, width: 0.018 + ((i * 7) % 5) * 0.007, a: 0.045 + ((i * 5) % 4) * 0.018,
  v: 0.09 + (i % 4) * 0.03, f: i * 1.9 }))
function rays(night: boolean): void {
  const c = g!
  const ox = W * ((night ? 0.82 : 0.12) + 0.03 * Math.sin(t * 0.05)), oy = -H * 0.18
  const long = Math.hypot(W, H) * 1.15
  const light = c.createRadialGradient(ox, oy, 0, ox, oy, long)
  if (night) { light.addColorStop(0, 'rgba(200,235,255,.55)'); light.addColorStop(0.35, 'rgba(120,200,255,.25)'); light.addColorStop(1, 'rgba(100,180,255,0)') }
  else { light.addColorStop(0, 'rgba(255,255,235,1)'); light.addColorStop(0.35, 'rgba(220,255,240,.55)'); light.addColorStop(1, 'rgba(200,255,240,0)') }
  for (const r of RAYS) {
    const mid = (night ? Math.PI - r.ang : r.ang) + 0.025 * Math.sin(t * r.v + r.f)
    const pulse = 0.65 + 0.35 * Math.sin(t * r.v * 1.7 + r.f)
    for (const [k, strength] of [[1, 0.35], [0.6, 0.6], [0.28, 1]]) {
      const half = r.width * k / 2
      c.beginPath(); c.moveTo(ox, oy)
      c.lineTo(ox + Math.cos(mid - half) * long, oy + Math.sin(mid - half) * long)
      c.lineTo(ox + Math.cos(mid + half) * long, oy + Math.sin(mid + half) * long)
      c.closePath()
      c.globalAlpha = r.a * strength * pulse
      c.fillStyle = light; c.fill()
    }
  }
  c.globalAlpha = 1
  radial(ox, oy, W * 0.55, night ? '170,220,255' : '255,255,230', night ? 0.18 : 0.35)
}
// Threads rising from the bottom left: from yellow (below) to green (above); aqua to violet at night
function fan(night: boolean): void {
  const c = g!, n = 26
  for (let i = 0; i < n; i++) {
    const k = i / (n - 1)
    const col = night ? (k < 0.5 ? [40, 235, 170] : [110, 120, 255]) : k < 0.5 ? [235, 245, 90] : [120, 235, 110]
    const a = (0.10 + 0.22 * (1 - Math.abs(k - 0.45) * 1.6)) * (i % 4 === 0 ? 1.8 : 1)
    const [x0, y0] = pt(-0.05, 0.92 - k * 0.12)
    const [c1x, c1y] = pt(0.25, 0.6 - k * 0.25, 0.02, 0.02, 0.25, i * 0.2)
    const [c2x, c2y] = pt(0.45, 0.78 - k * 0.1, 0.02, 0.03, 0.22, i * 0.3)
    const [x1, y1] = pt(1.05, 0.86 - k * 0.06, 0, 0.02, 0.2, i * 0.1)
    c.beginPath(); c.moveTo(x0, y0); c.bezierCurveTo(c1x, c1y, c2x, c2y, x1, y1)
    c.strokeStyle = `rgba(${col.join(',')},${Math.max(0, a)})`; c.lineWidth = i % 4 === 0 ? 2.2 : 1; c.stroke()
  }
}
// Soft glows at the bottom: yellow on the left, light blue on the right (no hard edge)
function glows(night: boolean): void {
  const list: [number, number, number, string, number][] = night
    ? [[0.02, 1.02, 0.5, '30,200,160', 0.3], [0.3, 1.08, 0.45, '40,140,200', 0.18], [0.98, 1.04, 0.5, '130,80,240', 0.3]]
    : [[0.02, 1.02, 0.5, '255,250,140', 0.42], [0.3, 1.08, 0.45, '170,240,120', 0.22], [0.98, 1.04, 0.5, '90,220,255', 0.38]]
  list.forEach(([x, y, r, col, a], i) => radial(W * (x + 0.02 * Math.sin(t * 0.15 + i)), H * y, W * r, col, a))
}
// A light-blue trail on the right: it enters from the bottom and leaves at the top right, always outside the window
function trail(night: boolean): void {
  const c = g!
  for (let i = 0; i < 8; i++) {
    const [x0, y0] = pt(0.58 + i * 0.012, 1.08)
    const [c1x, c1y] = pt(0.86, 0.92 - i * 0.01, 0.01, 0.015, 0.2, i)
    const [c2x, c2y] = pt(0.97, 0.45, 0.008, 0.03, 0.17, i)
    const [x1, y1] = pt(1.03 + i * 0.006, -0.08)
    c.beginPath(); c.moveTo(x0, y0); c.bezierCurveTo(c1x, c1y, c2x, c2y, x1, y1)
    c.strokeStyle = `rgba(${night ? (i % 2 ? '190,150,255' : '120,110,255') : i % 2 ? '150,250,255' : '90,220,255'},${0.26 - i * 0.02})`
    c.lineWidth = i === 0 ? 2.2 : 1.1; c.stroke()
  }
}

// ----- Blurred bubbles: blurred lights under the panels, swaying left and right very slowly (28-40 s each way). Their
// colours come from the main colour: a lighter shade, the colour itself, its hue turned -18° and +18°
const HALOS: [number, number, number, number, number][] = [[0.24, 0.3, 0.3, 0.38, 0.2], [0.78, 0.7, 0.34, 0.3, 0.17],
  [0.5, 1.05, 0.38, 0.16, 0.16], [0.92, 0.08, 0.22, 0.16, 0.22]]
const SWAY = [0.06, 0.008]                           // 6% of the width sideways, almost nothing up and down
/** Raises a colour until its strongest channel reaches `to` (bright colours stay as they are) */
const lift = (c: number[], to: number): number[] => {
  const m = Math.max(...c)
  return m >= to || !m ? c : c.map(v => Math.min(255, Math.round(v * to / m)))
}
/** The four halo colours ("r,g,b") of a main colour (#rrggbb). On a light page the lights lie on top of the page
 *  (no light that adds up), so they are raised to bright, clear shades instead of being darkened: with Light blurred
 *  bubbles' own colour they stay within a few shades of the old fixed turquoise lights (tests/appThemes.test.ts) */
export function haloColours(accent: string, light: boolean): string[] {
  const a = hex2rgb(accent), W: [number, number, number] = [255, 255, 255]
  const list = light
    ? [lift(a, 205), lift(a, 178), lift(shiftHue(a, -18), 182), lift(mixc(shiftHue(a, 18), W, 0.2), 210)]
    : [mixc(a, W, 0.3), a, shiftHue(a, -18), shiftHue(a, 18)]
  return list.map(c => c.join(','))
}
function halos(light: boolean, accent: string): void {
  const c = g!, cols = haloColours(accent, light)
  // On a light background light that adds up does not show: the halos lie on top, a little stronger
  c.globalCompositeOperation = light ? 'source-over' : 'lighter'
  HALOS.forEach(([x, y, r, a0, v], i) => {
    const col = cols[i], a = light ? a0 * 0.75 : a0
    const cx = W * (x + SWAY[0] * Math.sin(t * v + i * 2.1)), cy = H * (y + SWAY[1] * Math.cos(t * v * 1.3 + i))
    const gr = c.createRadialGradient(cx, cy, 0, cx, cy, Math.max(W, H) * r)
    gr.addColorStop(0, `rgba(${col},${a})`); gr.addColorStop(0.55, `rgba(${col},${a * 0.35})`); gr.addColorStop(1, `rgba(${col},0)`)
    c.fillStyle = gr; c.fillRect(0, 0, W, H)
  })
  c.globalCompositeOperation = 'source-over'
}

// ----- Nostalgia Channel: thin horizontal lines and a soft grey band at the bottom
function lines(night: boolean): void {
  const c = g!
  c.fillStyle = night ? 'rgba(255,255,255,.035)' : 'rgba(150,160,170,.10)'
  for (let y = 0; y < H; y += 6) c.fillRect(0, y, W, 1)
  const band = c.createLinearGradient(0, H * 0.82, 0, H), col = night ? '10,12,15' : '200,206,213'
  band.addColorStop(0, `rgba(${col},0)`); band.addColorStop(1, `rgba(${col},.55)`)
  c.fillStyle = band; c.fillRect(0, H * 0.82, W, H * 0.18)
}

// ----- Four Keys: a large green glow in the middle that breathes slowly; on white it lies on top, on dark green it adds up
function greenGlow(night: boolean): void {
  const c = g!
  const breath = 0.5 + 0.5 * Math.sin(t * 0.52)
  const cx = W * (0.5 + 0.03 * Math.sin(t * 0.18)), cy = H * 0.42, r = Math.max(W, H) * (0.5 + 0.04 * breath)
  const gr = c.createRadialGradient(cx, cy, 0, cx, cy, r)
  const col = night ? '47,181,58' : '60,176,67', a = (night ? 0.30 : 0.20) + 0.06 * breath
  gr.addColorStop(0, `rgba(${col},${a})`); gr.addColorStop(0.5, `rgba(${col},${a * 0.38})`); gr.addColorStop(1, `rgba(${col},0)`)
  c.globalCompositeOperation = night ? 'lighter' : 'source-over'
  c.fillStyle = gr; c.fillRect(0, 0, W, H)
  // A thread of brighter light at the bottom, like the base of a menu
  const base = c.createLinearGradient(0, H * 0.78, 0, H)
  base.addColorStop(0, `rgba(${col},0)`); base.addColorStop(1, `rgba(${col},${night ? 0.16 : 0.10})`)
  c.fillStyle = base; c.fillRect(0, H * 0.78, W, H * 0.22)
  c.globalCompositeOperation = 'source-over'
}
